import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Braces, Loader2, Plus, Trash2, X } from 'lucide-react';
import { analyticsHubService } from '../../../../../components/api/analyticsHubService';
import type { Theme, ThemeColors } from './types';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-900 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

interface ReportUsedEntitiesPopoverProps {
  theme?: Theme;
  reportDbId: string;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

function normalizeEntityNames(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => String(item).trim())
    .filter(Boolean);
}

const ReportUsedEntitiesPopover: React.FC<ReportUsedEntitiesPopoverProps> = ({
  theme = 'dark',
  reportDbId,
  anchorRef,
  onClose,
}) => {
  const colors = THEME_CONFIG[theme];
  const panelRef = useRef<HTMLDivElement>(null);
  const [entities, setEntities] = useState<string[]>([]);
  const [newEntity, setNewEntity] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEntities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsHubService.getReportDefinitionEntities(reportDbId);
      setEntities(normalizeEntityNames(response.data?.entities));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report entities.');
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [reportDbId]);

  useEffect(() => {
    void loadEntities();
  }, [loadEntities]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  const handleAddEntity = () => {
    const trimmed = newEntity.trim();
    if (!trimmed) return;
    if (entities.some((name) => name.toLowerCase() === trimmed.toLowerCase())) {
      setError('Entity name already exists.');
      return;
    }
    setEntities((prev) => [...prev, trimmed]);
    setNewEntity('');
    setError(null);
  };

  const handleRemoveEntity = (name: string) => {
    setEntities((prev) => prev.filter((item) => item !== name));
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await analyticsHubService.updateReportDefinitionEntities(reportDbId, {
        entities,
      });
      setEntities(normalizeEntityNames(response.data?.entities ?? entities));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update report entities.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-2 py-1.5 rounded-md border text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500/50 ${colors.input}`;

  return (
    <div
      ref={panelRef}
      className={`absolute right-0 top-full z-[80] mt-1.5 w-72 rounded-lg border shadow-2xl ${colors.border} ${colors.surface}`}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={`flex items-center justify-between px-3 py-2 border-b ${colors.border}`}>
        <div className="flex items-center gap-1.5">
          <Braces className="w-3.5 h-3.5 text-indigo-400" />
          <p className={`text-[11px] font-semibold ${colors.text.primary}`}>Used entities</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`p-0.5 rounded ${colors.hover} ${colors.text.muted}`}
          aria-label="Close entities panel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-3 py-2.5 space-y-2.5 max-h-56 overflow-y-auto">
        {loading && (
          <div className={`flex items-center justify-center gap-2 py-6 ${colors.text.muted}`}>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-[10px]">Loading…</span>
          </div>
        )}

        {!loading && entities.length === 0 && (
          <p className={`text-[10px] text-center py-4 ${colors.text.muted}`}>No entities linked yet.</p>
        )}

        {!loading &&
          entities.map((name) => (
            <div
              key={name}
              className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border ${colors.border} ${colors.secondary}`}
            >
              <code className={`text-[10px] font-mono truncate ${colors.text.secondary}`}>{name}</code>
              <button
                type="button"
                onClick={() => handleRemoveEntity(name)}
                className={`p-1 rounded ${colors.hover} text-red-400 shrink-0`}
                title={`Remove ${name}`}
                aria-label={`Remove ${name}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
      </div>

      <div className={`px-3 py-2.5 border-t ${colors.border} space-y-2`}>
        <div className="flex gap-1.5">
          <input
            type="text"
            value={newEntity}
            onChange={(event) => {
              setNewEntity(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAddEntity();
              }
            }}
            placeholder="Add entity name…"
            className={`flex-1 ${inputCls}`}
          />
          <button
            type="button"
            onClick={handleAddEntity}
            disabled={!newEntity.trim()}
            className="px-2 py-1.5 rounded-md border border-indigo-500/30 bg-indigo-600/15 text-indigo-400 hover:bg-indigo-600/25 disabled:opacity-40"
            title="Add entity"
            aria-label="Add entity"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

        {error && (
          <p className="text-[10px] text-red-400">{error}</p>
        )}

        <div className="flex justify-end gap-1.5 pt-0.5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`px-2.5 py-1 rounded-md text-[10px] ${colors.text.secondary} ${colors.hover} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={loading || saving}
            className="px-2.5 py-1 rounded-md text-[10px] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium"
          >
            {saving ? 'Saving…' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportUsedEntitiesPopover;
